import "yup-phone";

import { t, Trans } from "@lingui/macro";
import { CircularProgress, Divider, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import { Field, Form, Formik, FormikHelpers, FormikProps } from "formik";
import React from "react";
import * as yup from "yup";

import { DropdownField, InputField } from "@/components/shared/fields";
import { useSnackbar } from "@/components/shared/hooks";
import { notNullable } from "@/lib/util";
import { AccountError, CountryCode, ProfileGenderTypeEnum } from "@/saleor/api";

import { AddressFormData } from "../account/address/AddressForm";
import DateField from "../shared/fields/DateField";
// import { GENDERS } from "./ClientFilterDialog";

const CountryField = React.lazy(() => import("@/components/shared/fields/CountryField"));

export const contactSchema = yup
	.object({
		email: yup
			.string()
			.email()
			.label(t`Email`),
		firstName: yup
			.string()
			.label(t`First Name`)
			.required(),
		lastName: yup
			.string()
			.label(t`Last Name`)
			.notRequired(),
		dateOfBirth: yup.date().notRequired().nullable(),
		gender: yup.string().oneOf([null, "MALE", "FEMALE"]).nullable().notRequired(),
		address: yup
			.object({
				phone: yup
					.string()
					.label(t`Phone`)
					.phone("SG", true)
					.required(),
				streetAddress1: yup
					.string()
					.label(t`Street Address 1`)
					.required(),
				streetAddress2: yup
					.string()
					.label(t`Street Address 2`)
					.notRequired(),
				city: yup
					.string()
					.label(t`City`)
					.required(),
				country: yup
					.mixed<CountryCode>()
					.label(t`Country`)
					.required(),
				postalCode: yup
					.string()
					.label(t`Postal Code`)
					.required(),
				unitNumber: yup
					.string()
					.label(t`Unit No`)
					.notRequired(),
			})
			.required(),
	})
	.required();

// export type Contact = yup.InferType<typeof contactSchema>;
export type Contact = {
	email: string;
	firstName: string;
	lastName?: string;
	dateOfBirth?: moment.Moment;
	address: AddressFormData;
	gender?: "MALE" | "FEMALE" | "";
};

export type ContactFormProps = {
	onCompleted?: () => void;
	onCancel?: () => void;
	updateMutation: (contact: Contact) => Promise<AccountError[]>;
	existingData?: Contact;
	emailEditable?: boolean;
};

const ContactForm: React.VFC<ContactFormProps> = ({
	onCompleted,
	onCancel,
	existingData,
	updateMutation,
	emailEditable = true,
}) => {
	const GENDERS = [
		{
			value: "MALE",
			label: t`Male`,
		},
		{
			value: "FEMALE",
			label: t`Female`,
		},
	];
	const { showSnackbar } = useSnackbar();

	const initialValues = React.useMemo(() => {
		return (
			existingData || {
				email: "",
				firstName: "",
				lastName: "",
				dateOfBirth: undefined,
				gender: "" as ProfileGenderTypeEnum,
				address: {
					phone: "",
					firstName: "",
					lastName: "",
					streetAddress1: "",
					streetAddress2: "",
					country: CountryCode.Sg,
					city: "",
					postalCode: "",
					unitNumber: "",
				},
			}
		);
	}, [existingData]);

	const handleSubmitForm = async (values: Contact, helpers: FormikHelpers<Contact>) => {
		helpers.setSubmitting(true);

		const errors = await updateMutation(values);
		helpers.setSubmitting(false);

		if (errors.length === 0) {
			onCompleted && onCompleted();
		} else {
			errors.forEach((e) => {
				let fieldName = e.field || "";
				if (
					[
						"streetAddress1",
						"streetAddress2",
						"phone",
						"unitNumber",
						"country",
						"city",
						"postalCode",
						"dateOfBirth",
						"gender",
					].includes(fieldName)
				) {
					fieldName = `address.${fieldName}`;
				}
				fieldName &&
					helpers.setFieldError(fieldName, e.message || t`This field is required.`);
			});
			const message =
				errors
					.map((c) => c.message)
					.filter(notNullable)
					.join("\n") || t`There was an error creating contact! Please try again`;
			showSnackbar({
				message,
				severity: "error",
				horizontal: "center",
			});
		}
	};

	const renderForm = ({ isSubmitting, values, setFieldValue }: FormikProps<Contact>) => {
		return (
			<Form>
				<Stack
					direction="column"
					spacing={3}
					divider={<Divider orientation="horizontal" />}
				>
					<Stack direction="column" spacing={1.5}>
						<Stack direction="row" spacing={2}>
							<InputField name="firstName" label={t`First Name`} required />
							<InputField name="lastName" label={t`Last Name`} />
						</Stack>
						<Stack direction="row" spacing={2}>
							<InputField
								name="email"
								label={t`Email`}
								type="email"
								disabled={!emailEditable}
							/>
							<InputField name="address.phone" label={t`Phone`} type="tel" required />
						</Stack>
						<Stack direction="row" spacing={2}>
							<Field
								name="dateOfBirth"
								component={DateField}
								value={values.dateOfBirth}
								label={t`Date of Birth`}
								momentFormat="DD-MM-YYYY"
								onChange={(value: moment.Moment) => {
									console.log("dob value", value);
									setFieldValue("dateOfBirth", value);
								}}
								disablePast={false}
								disableFuture
								disableInput
							/>
							<DropdownField
								label={t`Gender`}
								data={GENDERS}
								name="gender"
								defaultText={t`Select...`}
							/>
						</Stack>
						<Stack direction="row" spacing={2}></Stack>

						<InputField
							name="address.streetAddress1"
							label={t`Street Address 1`}
							required
						/>
						<InputField name="address.streetAddress2" label={t`Street Address 2`} />

						<Stack direction="row" spacing={2}>
							<InputField name="address.unitNumber" label={t`Unit No`} />
							<React.Suspense fallback={<div />}>
								<CountryField
									name="address.country"
									label={t`Country`}
									required
									disabled
								/>
							</React.Suspense>
						</Stack>

						<Stack direction="row" spacing={2}>
							<InputField name="address.city" label={t`City`} required />
							<InputField
								name="address.postalCode"
								label={t`Postal Code`}
								required
								type="text"
							/>
						</Stack>
					</Stack>

					<Stack spacing={2} direction="row">
						<Button
							variant="outlined"
							fullWidth
							onClick={onCancel}
							disabled={isSubmitting}
						>
							<Trans>Cancel</Trans>
						</Button>
						<Button
							type="submit"
							variant="contained"
							fullWidth
							disabled={isSubmitting}
							endIcon={isSubmitting && <CircularProgress size={20} />}
						>
							<Trans>Save</Trans>
						</Button>
					</Stack>
				</Stack>
			</Form>
		);
	};

	return (
		<Formik<Contact>
			initialValues={initialValues}
			onSubmit={handleSubmitForm}
			validationSchema={contactSchema}
		>
			{renderForm}
		</Formik>
	);
};

export default ContactForm;
